import { Combobox, Textarea, useCombobox } from "@mantine/core";
import { useEffect, useRef, useState } from "react";

import classes from "./composer.module.css";
import type { Composer } from "./use-composer";

/** The "@" being typed before the caret, and what follows it. */
const TYPED_TAG = /@(\w*)$/;

export interface PromptBoxProps {
  c: Composer;
  /**
   * Live's prompt tags (#232, L04 to L04d): typing "@" opens a menu of them ("Image1", ...),
   * "Not Found" when the model tags none; Enter or a click puts "@Image1 " in. Without it the
   * prompt takes no tags (the image composer).
   */
  tags?: readonly string[];
}

/** The prompt: one line that grows to five, as live's (design-system.md 3.3). */
export function PromptBox({ c, tags }: PromptBoxProps) {
  const field = useRef<HTMLTextAreaElement>(null);
  const combobox = useCombobox();
  // The tag typed so far, or null when no "@" is being typed.
  const [typed, setTyped] = useState<string | null>(null);
  const offered =
    tags === undefined || typed === null
      ? []
      : tags.filter((tag) => tag.toLowerCase().startsWith(typed.toLowerCase()));
  const open = tags !== undefined && typed !== null;
  useEffect(() => {
    if (open) {
      combobox.openDropdown();
      // The first tag is ready for Enter, as live's.
      if (offered.length > 0) requestAnimationFrame(() => combobox.selectFirstOption());
    } else {
      combobox.closeDropdown();
    }
  }, [open, offered.length, combobox]);

  const insert = (tag: string) => {
    const element = field.current;
    const caret = element?.selectionStart ?? c.draft.prompt.length;
    const before = c.draft.prompt.slice(0, caret).replace(TYPED_TAG, `@${tag} `);
    c.set({ prompt: before + c.draft.prompt.slice(caret) });
    setTyped(null);
    // The caret goes after the tag, once the value is in.
    requestAnimationFrame(() => element?.setSelectionRange(before.length, before.length));
  };

  const textarea = (
    <Textarea
      ref={field}
      variant="unstyled"
      aria-label="Prompt"
      placeholder={`Describe your ${c.medium}...`}
      autosize
      minRows={1}
      maxRows={5}
      maxLength={c.promptMax}
      value={c.draft.prompt}
      onChange={(event) => {
        c.set({ prompt: event.currentTarget.value });
        if (tags === undefined) return;
        const caret = event.currentTarget.selectionStart;
        const match = TYPED_TAG.exec(event.currentTarget.value.slice(0, caret));
        setTyped(match === null ? null : (match[1] ?? ""));
      }}
      onBlur={() => setTyped(null)}
      onKeyDown={(event) => {
        if (open && event.key === "Escape") setTyped(null);
      }}
    />
  );
  if (tags === undefined) return textarea;
  return (
    <Combobox store={combobox} withinPortal={false} onOptionSubmit={insert}>
      <Combobox.Target>{textarea}</Combobox.Target>
      <Combobox.Dropdown className={classes.tags} aria-label="Tags">
        <Combobox.Options>
          {offered.length === 0 ? (
            <Combobox.Empty>Not Found</Combobox.Empty>
          ) : (
            offered.map((tag) => (
              <Combobox.Option key={tag} value={tag}>
                {tag}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
